import { i18n } from "~vue/i18n";
import { formattingLocale } from "~vue/i18n/formattingLocale";

// The closed set of ways a number is shown. Call sites say what kind of number it is; the active
// locale decides the decimal and grouping separators ("3.5" / "3,5", "1,234" / "1.234"). Adding a
// format is a design decision, not a per-site tweak (BRA-1047).
export type NumberFormat = "decimal" | "one-decimal" | "two-decimals" | "up-to-one-decimal" | "percent" | "signed-percent" | "compact";

const PRESETS: Record<NumberFormat, Intl.NumberFormatOptions> = {
    decimal: {},
    "one-decimal": { minimumFractionDigits: 1, maximumFractionDigits: 1 },
    "two-decimals": { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    "up-to-one-decimal": { maximumFractionDigits: 1 },
    // Percent formats take a fraction (0.35), not a percentage (35), like Intl itself.
    percent: { style: "percent", maximumFractionDigits: 0 },
    "signed-percent": { style: "percent", maximumFractionDigits: 0, signDisplay: "exceptZero" },
    compact: { notation: "compact", maximumFractionDigits: 1 },
};

const formatters = new Map<string, Intl.NumberFormat>();

export function formatNumberIn(value: number, format: NumberFormat, locale: string): string {
    const key = `${locale}|${format}`;
    let formatter = formatters.get(key);
    if (!formatter) {
        formatter = new Intl.NumberFormat(locale, PRESETS[format]);
        formatters.set(key, formatter);
    }
    return formatter.format(value);
}

// Reading the vue-i18n locale ref here is what makes a computed() over formatNumber re-render on
// a language switch. A missing value renders as nothing, like formatDate, so a template can write
// `formatNumber(avg, "one-decimal") || "—"` for its own placeholder.
export function formatNumber(value: number | null | undefined, format: NumberFormat = "decimal"): string {
    return value === null || value === undefined ? "" : formatNumberIn(value, format, formattingLocale(i18n.global.locale.value));
}
